/**
 * 작성자 : 홍선영
 * 날짜 : 2026.06.18
 * 기능 : 센서 종합현황 위젯(dCd 152) 공용 훅
 *  - useElementSize : ResizeObserver 로 컨테이너 실측 (대시보드 4열 / 상황판 5열 반응형 대응)
 *  - usePager       : N초마다 자동 전환되는 페이징 상태 (autoPlay ON 일 때 enabled=true)
 */
import { useEffect, useRef, useState } from 'react';

interface Size {
  width: number;
  height: number;
}

export const useElementSize = <T extends HTMLElement = HTMLDivElement>() => {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      // 소수점 흔들림으로 인한 무한 리렌더 방지 위해 정수로 반올림 후 변화 시에만 갱신
      setSize((prev) => {
        const next = { width: Math.round(width), height: Math.round(height) };
        if (prev.width === next.width && prev.height === next.height) return prev;
        return next;
      });
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, width: size.width, height: size.height };
};

interface UsePagerParams {
  pageCount: number;
  intervalMs: number;
  enabled: boolean; // autoPlay ON 일 때 true (대시보드·상황판 공통)
}

export const usePager = ({ pageCount, intervalMs, enabled }: UsePagerParams) => {
  const [page, setPage] = useState(0);

  // 페이지 수가 줄어들어 현재 페이지가 범위를 벗어나면 보정
  useEffect(() => {
    setPage((prev) => (prev >= pageCount ? 0 : prev));
  }, [pageCount]);

  // 자동 전환
  useEffect(() => {
    if (!enabled || pageCount <= 1) return undefined;
    const id = setInterval(() => {
      setPage((prev) => (prev + 1) % pageCount);
    }, intervalMs);
    return () => clearInterval(id);
  }, [enabled, pageCount, intervalMs]);

  const safePage = page >= pageCount ? 0 : page;
  return { page: safePage, setPage };
};
